import { Button, ButtonColor } from '@leland-dev/leland-ui-library';
import React, { useEffect, useRef } from 'react';

import { useDebouncedCallback } from '../../utils/hooks/useDebounce';

import LoadingIndicator from './LoadingIndicator';

type Props = Partial<Pick<IntersectionObserver, 'root' | 'rootMargin'>> & {
  className?: string;
  fetchMore: () => void;
  hasMore: boolean;
  loading: boolean;
};

const InfiniteScroll: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  fetchMore,
  hasMore,
  loading,
  root = null,
  rootMargin = '20px',
  children,
}) => {
  const loadingRef = useRef<HTMLDivElement>(null);

  const onFetchMore = useDebouncedCallback(fetchMore, 100);

  useEffect(() => {
    if (hasMore) {
      const handleObserver: IntersectionObserverCallback = (entities) => {
        const target = entities[0];
        if (target.isIntersecting) {
          onFetchMore();
        }
      };

      const observer = new IntersectionObserver(handleObserver, {
        root,
        rootMargin,
        threshold: 1.0,
      });

      if (loadingRef.current) {
        observer.observe(loadingRef.current);
      }

      return () => {
        observer.disconnect();
      };
    }
  }, [hasMore, onFetchMore, root, rootMargin]);

  return (
    <div className={`overflow-y-auto ${className}`}>
      {children}
      {hasMore ? (
        <div ref={loadingRef} className="mt-2 flex items-center justify-center">
          {loading ? (
            <LoadingIndicator iconClassName="w-12 h-12" />
          ) : (
            <Button
              label="Load more..."
              buttonColor={ButtonColor.WHITE}
              onClick={onFetchMore}
            />
          )}
        </div>
      ) : null}
    </div>
  );
};

export default InfiniteScroll;
